import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {DbActiveModal} from "../../../../modules/modal/modal-ref";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {Subject} from "rxjs";
import {UserSubscription, UserSubscriptionType} from "../../../../user/user-subscription";
import {UserService} from "../../../../user/user.service";
import {ChosenPlan} from "../../payment-info/models/plan.model";
import {TypeFormAnswers} from "../../../../add-website/typeFormAnswers";
import {combineLatest} from "rxjs";
import {modalSlideAnimation, scaleAnimation} from "../../../utils/animations";
import {AccountUpgradedDialogComponent} from "../account-upgraded-dialog/account-upgraded-dialog.component";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {GlobalEventDispatcherService} from "../../../services/global-event-dispatcher/global-event-dispatcher.service";
import {User} from "../../../../user/user";
import {TypeUtils} from "../../../utils/type-utils";
import {AuthService} from "../../../../auth/auth.service";
import {Utils} from "../../../utils/utils";
import {CookieService} from "ngx-cookie";

export type PaidFeatureUpgradeStep = 'features' | 'plans' | 'checkout';
export type PaidFeatureUpgradeMode = 'ssh' | 'cloudflare' | 'regenerate' | 'pro-level' | 'passwordProtect' | 'professional';

@Component({
    selector: 'db-paid-feature-upgrade',
    templateUrl: './paid-feature-upgrade.component.html',
    styleUrls: ['./paid-feature-upgrade.component.scss'],
    animations: [modalSlideAnimation, scaleAnimation],
    standalone: false
})
export class PaidFeatureUpgradeComponent implements OnInit, OnDestroy {

  @Input() mode: PaidFeatureUpgradeMode = 'ssh';
  @Input() buyAdditionalWord?: boolean;
  @Input() isEcommerce?: boolean;
  public userSubscription: UserSubscription;
  public chosenPlan: ChosenPlan;
  public step: PaidFeatureUpgradeStep = 'features';
  public isAgency = false;
  public user: User;
  public expirationDate: string;
  private _isDemoUser: boolean;
  public newPlans: boolean = true;
  public englishCountries: boolean = true;

  public readonly userSubscriptionType = UserSubscriptionType;
  private readonly _destroyStream$ = new Subject<boolean>();

  constructor(
    private readonly activeModal: DbActiveModal,
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _userService: UserService,
    private readonly _dialogService: DialogService,
    private readonly _globalEventDispatcher: GlobalEventDispatcherService,
    private readonly _authService: AuthService,
    private readonly _cookieService: CookieService,
  ) { }

  ngOnInit() {
    this._getUserSubscription();
    this.resize();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public resize(): void {
    if (this.step === 'features') {
      this.activeModal.resize(600,  940);
    }
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getUserLocalStorage),
        )),
        filter(([user, userSubscription]) => !!user && !!userSubscription),
        takeUntil(this._destroyStream$),
      )
      .subscribe(([user, userSubscription, userLocalStorage]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.englishCountries = userLocalStorage && !!userLocalStorage.tier1B;
        this._isDemoUser = Utils.isDemoUser(userSubscription, user);
        if (userSubscription.type !== UserSubscriptionType.FREE && userSubscription.plan) {
          this.setNextBillingDate(!TypeUtils.isNull(userSubscription.nextBillingDate) ? userSubscription.nextBillingDate : new Date(), userSubscription.plan.periodNumber > 1 ? 365 : 30);
        }
        this.isAgency = user.signUpFor && user.signUpFor.Role
          && [TypeFormAnswers.AGENCY].includes(user.signUpFor.Role.id) || false;
        this.chosenPlan = {
          id: this.userSubscription.subscriptionId,
          title: this.userSubscription.title,
          type: this.userSubscription.plan && this.userSubscription.plan.periodNumber === 1 ? 'month' : 'year',
          price: this.userSubscription.plan && this.userSubscription.plan.price,
        };
      });
  }
  public closeModal(isUpgraded = true): void {
    if (isUpgraded || this._isDemoUser) {
      this._showSuccessfullyUpgradedDialog();
    }
    this.activeModal.close();
  }

  private _showSuccessfullyUpgradedDialog(): void {
    this._dialogService.addDialog(AccountUpgradedDialogComponent, {
      currentSubscription: this.userSubscription,
      upgradedFromTrial: this.userSubscription && this.userSubscription.trialClient,
      upgradedFromBooster: this.userSubscription && this.userSubscription.boosterProClient
    });
  }


  public changePlan(plan: ChosenPlan): void {
    this.chosenPlan = plan;
    const date = new Date();
    this.setNextBillingDate(date, plan.type === 'month' ? 30 : 365);
  }

  public setNextBillingDate(date: Date | string, planPeriod: number): void {
    let _date: Date;

    if (date instanceof Date) {
      _date = new Date(date.getTime()); // Clone the date if it's already a Date object
    } else {
      _date = new Date(date); // Assuming 'date' is a string representation of a date
    }

    if (planPeriod === 30) {
      _date.setUTCDate(_date.getUTCDate() + planPeriod);
    } else {
            _date.setUTCFullYear(_date.getUTCFullYear() + 1);
    }

    this.expirationDate = _date.toUTCString();
  }

}
